import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, Download, ArrowLeft } from 'lucide-react';
import CheckoutBrandHeader from '../../components/public/CheckoutBrandHeader';
import { getBackendUrl } from '../../lib/env';

const API_URL = getBackendUrl();

function formatMoney(currency, value) {
  const parsed = Number(value || 0);
  return `${currency || 'UGX'} ${Number.isNaN(parsed) ? value : parsed.toLocaleString()}`;
}

export default function PackageReceipt() {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get('ref') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!reference) {
      setLoading(false);
      return;
    }
    axios.get(`${API_URL}/api/pay/package-receipts/${reference}`)
      .then((r) => setData(r.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [reference]);

  const downloadReceipt = () => {
    if (!data) return;
    const lines = [
      'JJUMA PAY PACKAGE RECEIPT',
      `Package: ${data.package?.name || 'Subscription'}`,
      `Plan: ${(data.subscription?.billing_cycle || 'monthly').toUpperCase()}`,
      `Reference: ${data.transaction?.reference_code || reference}`,
      `Transaction ID: ${data.transaction?.transaction_id || ''}`,
      `Amount: ${formatMoney(data.transaction?.currency, data.transaction?.amount)}`,
      `Status: ${data.transaction?.status || 'successful'}`,
      `Activated: ${data.subscription?.subscription_start || data.transaction?.updated_at || ''}`,
      `Expires: ${data.subscription?.subscription_end || ''}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jjuma-pay-receipt-${reference || 'package'}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printReceipt = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
        <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!reference || !data) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-xl bg-white border border-slate-200 rounded-[28px] shadow-sm">
          <CardContent className="p-8 text-center space-y-4">
            <p className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Receipt not found</p>
            <p className="text-sm text-slate-500">We couldn’t load this package receipt.</p>
            <Link to="/dashboard/package">
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <ArrowLeft size={14} className="mr-1" /> Back to packages
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pkg = data.package || {};
  const sub = data.subscription || {};
  const txn = data.transaction || {};
  const branding = data.branding || {};

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-6">
        <style>{`
          @media print {
            body { background: #fff !important; }
            .receipt-hide-print { display: none !important; }
            .receipt-print-card { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
          }
        `}</style>
        <div className="rounded-[28px] border border-emerald-100 bg-white/90 shadow-sm backdrop-blur px-5 py-6">
          <CheckoutBrandHeader branding={branding} compact className="pt-1" />
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-emerald-50 px-4 py-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">Package receipt</p>
              <p className="mt-1 text-sm text-slate-600">
                Billed to <span className="font-medium text-slate-900">{data.owner_name || branding.brand_tagline || 'Jjuma Pay customer'}</span>
              </p>
            </div>
            <div className="rounded-full bg-white px-3 py-1 text-xs font-medium text-emerald-700 border border-emerald-100">
              {pkg.name || 'Subscription'}
            </div>
          </div>
        </div>
        <Card className="receipt-print-card bg-white border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.08)] rounded-[28px] overflow-hidden">
          <CardContent className="p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-2 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Package Activated
            </h1>
            <p className="text-sm text-slate-500 text-center mb-6">
              Your subscription is active and ready to use.
            </p>
            <div className="mb-6 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-center">
              <p className="text-xs uppercase tracking-[0.22em] text-emerald-700">Powered by Jjuma Pay</p>
              <p className="mt-1 text-sm text-slate-600">
                Receipt reference <span className="font-medium text-slate-900">{txn.reference_code || reference}</span>
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ['Package', pkg.name || 'Subscription'],
                ['Billing Cycle', (sub.billing_cycle || 'monthly').toUpperCase()],
                ['Amount', formatMoney(txn.currency, txn.amount)],
                ['Reference', txn.reference_code || reference],
                ['Transaction ID', txn.transaction_id || '-'],
                ['Status', txn.status || 'successful'],
                ['Activated At', sub.subscription_start || txn.updated_at || '-'],
                ['Expires At', sub.subscription_end || '-'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="mt-1 text-sm font-medium text-slate-900 break-all">{value}</p>
                </div>
              ))}
            </div>

            <div className="receipt-hide-print mt-6 flex flex-wrap gap-3 justify-center">
              <Button type="button" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={printReceipt}>
                <Download size={14} className="mr-1" /> Print / Save PDF
              </Button>
              <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={downloadReceipt}>
                <Download size={14} className="mr-1" /> Download Receipt
              </Button>
              <Link to="/dashboard/package">
                <Button type="button" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <ArrowLeft size={14} className="mr-1" /> Back to Package
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
